import type { Container, Newable } from "inversify";
import { Logger, Module } from "@designboticteam/core";
import { MaterialControlService } from "./appearance/MaterialControlService";
import { MaterialFactoryService } from "./appearance/MaterialFactoryService";
import { AssetLoaderService } from "./exchange/ImportService/AssetLoaderService";
import { CursorSnappingService } from "./input/CursorSnappingService";
import { RaycastService } from "./input/RaycastService";
import { PerformanceMonitorService } from "./rendering/PerformanceMonitorService";
import { DefaultRendererService, type IRendererService, RENDERER_SERVICE } from "./rendering/RendererService";
import { type LabelsDisplayMode, Workspace3DStoreService } from "./services/Workspace3DStoreService";
import { ShapeEditingService } from "./sketching/services/ShapeEditingService";
import { ShapeTool } from "./sketching/tools/create/_base/ShapeTool";
import { CircleTool } from "./sketching/tools/create/CircleTool";
import { LineSegmentTool } from "./sketching/tools/create/LineSegmentTool";
import { PolygonTool } from "./sketching/tools/create/PolygonTool";
import { PolylineTool } from "./sketching/tools/create/PolylineTool";
import { EdgeEditHandler } from "./sketching/tools/modify/EdgeEditHandler";
import { SelectTool } from "./sketching/tools/modify/SelectTool";
import { VertexEditHandler } from "./sketching/tools/modify/VertexEditHandler";
import { CAMERA_CONTROL_SERVICE, type ICameraControlService, OrbitCameraControlService } from "./stage/CameraControlService";
import { CameraService } from "./stage/CameraService";
import { CameraViewService } from "./stage/CameraViewService";
import { SceneService } from "./stage/SceneService";
import { type ToolClass, ToolsManagerService } from "./tooling/ToolsManagerService";

type Workspace3DModuleConfig = {
	rendererService?: Newable<IRendererService>;
	cameraControlService?: Newable<ICameraControlService>;
};

type WorkspaceToolClass = typeof SelectTool | typeof PolylineTool | typeof LineSegmentTool | typeof PolygonTool | typeof CircleTool;

const BUILT_IN_TOOLS: Array<ToolClass> = [SelectTool, PolylineTool, LineSegmentTool, PolygonTool, CircleTool];

class Workspace3DModule extends Module {
	public readonly moduleName = "Workspace3DModule";
	private readonly logger = new Logger("🪩 Workspace3DModule", { color: "#ffffff", backgroundColor: "#00ff00" });

	constructor(
		private readonly config: Workspace3DModuleConfig = {
			rendererService: DefaultRendererService,
			cameraControlService: OrbitCameraControlService,
		},
	) {
		super();
		this.services = [
			Workspace3DStoreService,
			//
			SceneService,
			CameraService,
			CameraViewService,
			//
			PerformanceMonitorService,
			RaycastService,
			//
			CursorSnappingService,
			// Before ToolsManagerService: its dispose deactivates the active tool, which resets this service.
			ShapeEditingService,
			//
			AssetLoaderService,
			MaterialFactoryService,
			MaterialControlService,
			//
			ToolsManagerService,
		];
	}

	public override register(container: Container): void {
		super.register(container);

		container
			.bind(RENDERER_SERVICE)
			.to(this.config.rendererService ?? DefaultRendererService)
			.inSingletonScope();

		if (this.config.cameraControlService) {
			container.bind(CAMERA_CONTROL_SERVICE).to(this.config.cameraControlService).inSingletonScope();
		} else {
			container.bind(CAMERA_CONTROL_SERVICE).to(OrbitCameraControlService).inSingletonScope();
		}

		for (const Tool of BUILT_IN_TOOLS) {
			container.bind(Tool).toSelf().inSingletonScope();
		}

		// Edit operations (IShapeEditHandler) are composed by ShapeEditingService and driven
		// directly — not manager-activated tools — so bind them without registering them.
		container.bind(VertexEditHandler).toSelf().inSingletonScope();
		container.bind(EdgeEditHandler).toSelf().inSingletonScope();
	}

	public override initialize(): void {
		super.initialize();

		this.container.get<IRendererService>(RENDERER_SERVICE).initialize();
		this.container.get<ICameraControlService>(CAMERA_CONTROL_SERVICE).initialize();

		const toolsManagerService = this.getService(ToolsManagerService);

		for (const Tool of BUILT_IN_TOOLS) {
			toolsManagerService.registerTool(this.container.get(Tool));
		}

		this.logger.log("initialized");
	}

	public override dispose(): void {
		if (this.container.isBound(CAMERA_CONTROL_SERVICE)) {
			this.container.get<ICameraControlService>(CAMERA_CONTROL_SERVICE).dispose();
		}

		super.dispose();
	}

	public get api() {
		return {
			workspace3D: {
				store: this.getService(Workspace3DStoreService).store,
				statsPanel: {
					getStatsPanelElement: this.getService(PerformanceMonitorService).getStatsPanelElement.bind(this.getService(PerformanceMonitorService)),
					show: this.getService(PerformanceMonitorService).showStatsPanel.bind(this.getService(PerformanceMonitorService)),
					hide: this.getService(PerformanceMonitorService).hideStatsPanel.bind(this.getService(PerformanceMonitorService)),
				},
				cursor: {
					setSnappingEnabled: this.getService(CursorSnappingService).setSnappingEnabled.bind(this.getService(CursorSnappingService)),
				},
				tools: {
					setActiveTool: (Tool: WorkspaceToolClass | null) => {
						this.getService(ToolsManagerService).setActiveTool(Tool);
					},
					cancelCurrentSketch: () => {
						const activeTool = this.getService(ToolsManagerService).activeTool;

						if (activeTool instanceof ShapeTool) {
							activeTool.cancelDrawing();
						}
					},
					selectAllEdges: () => {
						this.getService(ShapeEditingService).selectAllEdges();
					},
					clearEdgeSelection: () => {
						this.getService(ShapeEditingService).clearEdgeSelection();
					},
					setEdgeOffset: (distance: number) => {
						this.getService(ShapeEditingService).setEdgeOffset({ distance });
					},
					setNameLabelsDisplayMode: (labelsDisplayMode: LabelsDisplayMode) => {
						this.getService(Workspace3DStoreService).store.setState((state) => ({ tools: { ...state.tools, nameLabelsDisplayMode: labelsDisplayMode } }));
					},
					setDimensionLabelsDisplayMode: (labelsDisplayMode: LabelsDisplayMode) => {
						this.getService(Workspace3DStoreService).store.setState((state) => ({ tools: { ...state.tools, dimensionLabelsDisplayMode: labelsDisplayMode } }));
					},
				},
			},
		};
	}
}

export { Workspace3DModule, type Workspace3DModuleConfig, type WorkspaceToolClass };
